import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from "react";
import { Checkbox, Link, Typography } from "@material-ui/core";
import { FormattedDate, FormattedNumber } from "react-intl";
import { isDate, isNumeric } from "../utils";
import { FieldType } from "../tableSchema";
import { DployTextField, NumberField, SelectField, CheckboxWithLabelField, CurrencyField, PercentField, KeyboardDatePickerField } from "@ploy-ui/form-fields";
import { useField } from "formik";
import { ErrorDisplay } from "@ploy-lib/types";
const getItemValue = (item, optionSource) => {
    let keyLabelToReturn;
    if (typeof item === "object") {
        keyLabelToReturn = item?.key
            ? item.key
            : item?.[optionSource?.key ?? "key"];
    }
    else {
        keyLabelToReturn = item;
    }
    return keyLabelToReturn?.toString();
};
const getItemLabel = (item, optionSource) => {
    let itemValueToReturn;
    if (typeof item === "object") {
        itemValueToReturn = item?.value
            ? item.value
            : item?.[optionSource?.value ?? "value"];
    }
    else {
        itemValueToReturn = item;
    }
    return itemValueToReturn?.toString();
};
const isValidNumber = (value) => {
    return value !== "" && value !== undefined && value !== null && !isNaN(value);
};
export const SimpleTextLiteral = (props) => {
    const { value, items, multiple, hrefLink, fieldMetadata, tc, ...rest } = props;
    let valueForDisplay = value;
    if (hrefLink) {
        return (_jsx(Typography, { variant: "subtitle2", style: {
                fontWeight: "inherit",
                width: props.tc.width ?? "100%"
            }, "data-cr-component": "LinkLiteral", ...rest, children: _jsx(Link, { href: hrefLink, underline: "hover", children: valueForDisplay }) }));
    }
    if (fieldMetadata) {
        return _jsx(SchemaField, { ...props });
    }
    if (multiple) {
        valueForDisplay = Array.isArray(value)
            ? value
                ?.map(v => {
                const item = items?.find(x => getItemValue(x) === getItemValue(v));
                return item && getItemLabel ? getItemLabel(item) : getItemLabel(v);
            })
                .join(",\n")
            : value;
    }
    else if ((items?.length ?? 0) > 0) {
        const item = items?.find(x => getItemValue(x) === getItemValue(value));
        valueForDisplay = item ? getItemLabel(item) : value;
    }
    else if (isNumeric(value)) {
        const numericFieldValue = Number(value);
        return (_jsx(Typography, { variant: "subtitle2", style: {
                fontWeight: "inherit",
                textAlign: "right",
                width: props.tc.width ?? "100%"
            }, "data-cr-component": "NumberLiteral", ...rest, children: _jsx(FormattedNumber, { value: numericFieldValue }) }));
    }
    else if (isDate(value)) {
        const dateFieldValue = new Date(value.toString());
        return (_jsx(Typography, { variant: "subtitle2", style: {
                fontWeight: "inherit",
                textAlign: "right",
                width: props.tc.width ?? "100%"
            }, "data-cr-component": "DateLiteral", ...rest, children: _jsx(FormattedDate, { value: dateFieldValue, format: "short_numeric" }) }));
    }
    return (_jsx(Typography, { variant: "subtitle2", style: {
            fontWeight: "inherit",
            width: props.tc.width ?? "100%",
            whiteSpace: multiple ? "pre-wrap" : "normal"
        }, "data-cr-component": "StringLiteral", ...rest, children: valueForDisplay }));
};
// Select,
// Multiselect,
// Text,
// Number,
// Currency,
// Percent
const SchemaField = (props) => {
    const { value, multiple, items, fieldMetadata = {}, tc, ...rest } = props;
    const dataCrComponent = FieldType[fieldMetadata.fieldType] + "Literal";
    const isMultiline = fieldMetadata.isMultiline ?? multiple;
    switch (fieldMetadata.fieldType) {
        case FieldType.Percent:
            return (_jsx(Typography, { variant: "subtitle2", style: {
                    fontWeight: "inherit",
                    textAlign: "right",
                    width: props.tc.width ?? "100%"
                }, "data-cr-component": dataCrComponent, ...rest, children: isValidNumber(value) ? (_jsx(FormattedNumber, { value: value / 100, format: "percent", maximumFractionDigits: fieldMetadata.maximumFractionDigits ?? 2, minimumFractionDigits: fieldMetadata.minimumFractionDigits ?? 2 })) : ("") }));
        case FieldType.Currency:
        case FieldType.Number:
            //Currency is currently identical to number, but there exists posibilities format currencies differently in the future
            return (_jsx(Typography, { variant: "subtitle2", style: {
                    fontWeight: "inherit",
                    textAlign: "right",
                    width: props.tc.width ?? "100%"
                }, "data-cr-component": dataCrComponent, ...rest, children: isValidNumber(value) ? (_jsx(FormattedNumber, { value: value, maximumFractionDigits: fieldMetadata.maximumFractionDigits ?? 0, minimumFractionDigits: fieldMetadata.minimumFractionDigits ?? 0 })) : (`${value === "-" ? "-" : ""}` // Support displaying a "N/A"-like value instead of empty cell for columns where this might be applicable
                ) }));
        case FieldType.Multiselect:
            const multiValue = Array.isArray(value)
                ? value
                    ?.map(v => {
                    const item = items?.find(x => getItemValue(x) === getItemValue(v));
                    return item && getItemLabel
                        ? getItemLabel(item)
                        : getItemLabel(v);
                })
                    .join(",\n")
                : value;
            return (_jsx(Typography, { variant: "subtitle2", style: {
                    fontWeight: "inherit",
                    width: props.tc.width ?? "100%",
                    whiteSpace: isMultiline ? "pre-wrap" : "normal"
                }, "data-cr-component": dataCrComponent, ...rest, children: multiValue }));
        case FieldType.Select:
            const item = items?.find(x => getItemValue(x) === getItemValue(value));
            const selectValue = item ? getItemLabel(item) : value;
            return (_jsx(Typography, { variant: "subtitle2", style: {
                    fontWeight: "inherit",
                    width: props.tc.width ?? "100%",
                    whiteSpace: isMultiline ? "pre-wrap" : "normal"
                }, "data-cr-component": dataCrComponent, ...rest, children: selectValue }));
        case FieldType.Date:
            if (!isDate(value))
                return null;
            const dateFieldValue = new Date(value.toString());
            return (_jsx(Typography, { variant: "subtitle2", style: {
                    fontWeight: "inherit",
                    textAlign: "right",
                    width: props.tc.width ?? "100%"
                }, "data-cr-component": dataCrComponent, ...rest, children: _jsx(FormattedDate, { value: dateFieldValue, format: "short_numeric" }) }));
        case FieldType.Checkbox:
            return _jsx(Checkbox, { checked: value, disabled: true });
        default:
            return (_jsx(Typography, { variant: "subtitle2", style: {
                    fontWeight: "inherit",
                    width: props.tc.width ?? "100%",
                    whiteSpace: isMultiline ? "pre-wrap" : "normal"
                }, "data-cr-component": "StringLiteral", ...rest, children: value }));
    }
};
export const EditingSchemaField = (props) => {
    const { fieldId, form, fieldMetadata, tc, row, tableData } = props;
    const [field, meta] = useField(`${fieldId}`);
    const error = !!form?.errors?.[fieldId];
    const isDisabled = useMemo(() => {
        // TableFields will only be marked disabled if they have a metadata-editableFunction that returns `false`
        const hasIsEditableFunction = fieldMetadata?.isEditableFunctions;
        if (hasIsEditableFunction) {
            const func = Object.values(fieldMetadata.isEditableFunctions)[0];
            const isEditable = func(form, row.cells, tableData);
            return !isEditable;
        }
        return false;
    }, [fieldMetadata?.isEditableFunctions, form, row.cells, tableData]);
    if (!field)
        return null;
    switch (fieldMetadata?.fieldType) {
        case FieldType.Currency:
        case FieldType.Percent:
        case FieldType.Number:
            let Component = NumberField;
            let formatString = "#,###";
            if (fieldMetadata.fieldType === FieldType.Percent) {
                Component = PercentField;
                formatString = undefined;
            }
            if (fieldMetadata.fieldType === FieldType.Currency)
                Component = CurrencyField;
            return (_jsx(Component, { formatString: formatString, "data-cr-field": field.name, "data-cr-component": "input", disabled: isDisabled, variant: "standard", size: "small", field: field, form: form, meta: meta, cssStyle: {
                    fontWeight: "inherit",
                    textAlign: "right",
                    width: props.tc.width ?? "100%"
                }, emptyEqualsZero: fieldMetadata?.fieldType !== FieldType.Percent &&
                    fieldMetadata?.fieldType !== FieldType.Number &&
                    !fieldMetadata?.emptyIsNAN, fullWidth: true, error: error, allowNegative: fieldMetadata?.allowNegative, maximumFractionDigits: fieldMetadata?.maximumFractionDigits ??
                    (fieldMetadata?.fieldType === FieldType.Percent ? 2 : 0), errorDisplay: ErrorDisplay.AlwaysNoMessage, onBlur: e => fieldMetadata?.alwaysInEditMode && form.submitForm() }));
        case FieldType.Checkbox:
            return (_jsx(CheckboxWithLabelField, { "data-cr-field": field.name, "data-cr-component": "CheckboxField", style: {
                    width: props.tc.width ?? "100%"
                }, disabled: isDisabled, field: field, form: form, meta: meta, error: error, errorDisplay: ErrorDisplay.AlwaysNoMessage, onChange: (e, checked) => {
                    form.setFieldValue(field.name, checked);
                    fieldMetadata?.alwaysInEditMode && form.submitForm();
                } }));
        case FieldType.Select:
        case FieldType.Multiselect:
            const multipleSelect = fieldMetadata?.fieldType === FieldType.Multiselect ||
                tc.isMultipleSelect;
            return (_jsx(SelectField, { "data-cr-field": field.name, "data-cr-component": "SelectionField", style: {
                    width: props.tc.width ?? "100%"
                }, disabled: isDisabled, variant: "standard", size: "small", field: field, form: form, meta: meta, error: error, errorDisplay: ErrorDisplay.AlwaysNoMessage, disableClearable: multipleSelect, searchable: true, multiple: multipleSelect, items: tc.optionValues
                    ?.filter(item => item.key || item.value)
                    .sort((a, b) => a.value.localeCompare(b.value)) || [], getItemLabel: (item) => getItemLabel(item, tc.optionSource), getItemValue: (item) => getItemValue(item, tc.optionSource), fullWidth: true, minWidthFocused: 200 }));
        case FieldType.Date:
            return (_jsx(KeyboardDatePickerField, { "data-cr-field": field.name, "data-cr-component": "KeyboardDatePickerField", style: {
                    width: props.tc.width ?? "100%"
                }, disabled: isDisabled, pickerVariant: "dialog", variant: "standard", form: form, field: field, meta: meta, error: error, errorDisplay: ErrorDisplay.AlwaysNoMessage, clearable: true, allowNull: !Boolean(fieldMetadata.isRequired), fullWidth: true, onChange: (event) => {
                    form.setFieldValue(field.name, event.target.value);
                } }));
        default:
            return (_jsx(DployTextField, { "data-cr-field": field.name, "data-cr-component": "TextField", style: {
                    width: props.tc.width ?? "100%"
                }, disabled: isDisabled, variant: "standard", size: "small", ...field, fullWidth: true, error: error }));
    }
};
//# sourceMappingURL=TableFields.js.map